/**
 * Command System Types
 *
 * Defines the command interface that powers all user interactions.
 * Commands are the single abstraction for actions - whether triggered
 * via keyboard shortcuts, command palette, context menus, or programmatically.
 */

import type { UINode } from '@rool/renderer';

/**
 * Command categories for grouping in UI.
 */
export type CommandCategory =
	| 'Canvas' // Canvas-wide operations
	| 'Canvas (Selection)' // Operations on selected nodes
	| 'Text (Selection)' // Operations on selected text
	| 'Edit' // Clipboard, undo/redo
	| 'View' // Zoom, fit, pan
	| 'Tools' // Tool activation
	| 'Application' // Global app operations
	| 'Debug'; // Development/debug tools

/**
 * Command definition.
 * Commands are named, discoverable actions that can be triggered from multiple places.
 */
export interface Command {
	/** Unique identifier (e.g., 'edit.copy', 'view.zoom-in') */
	id: string;

	/** Display label for UI */
	label: string;

	/** Category for grouping */
	category: CommandCategory;

	/** Keyboard shortcuts (e.g., ['Cmd+C']) - informational only, actual binding is in InputSystem */
	shortcuts?: string[];

	/** Description for help/tooltips */
	description?: string;

	/** Sub-grouping within category for context menu sections */
	section?: string;

	/** Submenu label — commands sharing the same submenu are grouped into a flyout */
	submenu?: string;

	/** Show in context menu (default: true) */
	showInContextMenu?: boolean;

	/** Icon name (for UI display) */
	icon?: string;

	/**
	 * Execute the command.
	 * Called when the command is triggered.
	 */
	execute: () => void | Promise<void>;

	/**
	 * Get dynamic label based on selection.
	 * Return false to hide the command from context menu / palette.
	 * If not provided, the command is always visible with static label.
	 */
	getLabel?: (selectedNodes: UINode[]) => string | false;

	/**
	 * Get checked state for checkbox-style menu items.
	 * Returns true (checked), false (unchecked), or 'mixed' (indeterminate).
	 * When defined, the command renders as a checkbox in context menus.
	 */
	checked?: (selectedNodes: UINode[]) => boolean | 'mixed';
}

/**
 * Metadata for command registration.
 * Used to register commands with the local registry.
 */
export interface CommandMeta {
	/** Display label for UI */
	label: string;

	/** Category for grouping */
	category: CommandCategory;

	/** Description for help/tooltips */
	description?: string;

	/** Sub-grouping within category for context menu sections */
	section?: string;

	/** Submenu label — commands sharing the same submenu are grouped into a flyout */
	submenu?: string;

	/** Show in context menu (default: true) */
	showInContextMenu?: boolean;

	/** Icon name (for UI display) */
	icon?: string;

	/**
	 * Get dynamic label based on selection.
	 * Return false to hide the command.
	 */
	getLabel?: (selectedNodes: UINode[]) => string | false;

	/**
	 * Get checked state for checkbox-style menu items.
	 * Returns true (checked), false (unchecked), or 'mixed' (indeterminate).
	 * When defined, the command renders as a checkbox in context menus.
	 */
	checked?: (selectedNodes: UINode[]) => boolean | 'mixed';
}

/**
 * Context menu position for rendering.
 */
export interface ContextMenuPosition {
	x: number;
	y: number;
}

/**
 * Context menu state for the context menu component.
 */
export interface ContextMenuState {
	visible: boolean;
	position: ContextMenuPosition;
	/** World coordinates where the menu was opened */
	worldPosition?: { x: number; y: number };
}

/**
 * Mode display names for UI.
 * Maps InputSystem mode names to human-readable labels.
 * Empty string means don't show a header for that mode.
 */
export const MODE_DISPLAY_NAMES: Record<string, string> = {
	default: '', // Don't show for default
	connecting: 'Connecting Mode',
	'box-select': 'Box Selecting',
	drag: 'Dragging',
	resize: 'Resizing',
	rotating: 'Rotating',
	editing: 'Editing Text',
};
